// The Memberships place's words and sums (M8), kept apart from the screen so they are tested.

export interface Member {
  id: string;
  customerId: string;
  customerName: string;
  address: string;
  planName: string;
  status: MemberStatus;
  /** BL-056: cancelled from a date to come (it won't renew), or on one. */
  cancelsOn?: string | null;
  cancelledOn?: string | null;
  startsOn: string;
  endsOn: string;
  autoRenew: boolean;
  visitsTotal: number;
  visitsUsed: number;
  nextDueOn: string | null;
  /** This term's renewal (BL-009): its state and the latest thing to know, or null before one starts. */
  renewal?: { state: RenewalState; note: string | null } | null;
  /** Billed by instalments (BL-009): "Bills $20.00 monthly, next on Dec 3, 2026.", and a bill not paid. */
  billing?: { summary: string; atRisk: boolean } | null;
}

/** Where a term's renewal stands (memberships.renew, BL-009 phase 3). */
export type RenewalState = 'scheduled' | 'retrying' | 'unknown' | 'renewed' | 'at_risk' | 'stopped';

/** A chip's tone (statusWords, RENEWAL_WORDS, BILL_WORDS) as Blueprint draws the word (DESIGN.md §2):
 * none muted, ok in ink, warn dashed under (it waits), bad red. Label's and DataTable's words. */
export type Tone = '' | 'ok' | 'warn' | 'bad';
export const labelKind = (t: Tone): 'muted' | 'now' | 'wait' | 'bad' =>
  t === 'ok' ? 'now' : t === 'warn' ? 'wait' : t === 'bad' ? 'bad' : 'muted';
export const wordTone = (t: Tone): 'muted' | 'ink' | 'wait' | 'bad' =>
  t === 'ok' ? 'ink' : t === 'warn' ? 'wait' : t === 'bad' ? 'bad' : 'muted';

/** Each state in a word or three, with the tone of its chip; the server's note says the rest. */
export const RENEWAL_WORDS: Record<RenewalState, [string, '' | 'ok' | 'bad' | 'warn']> = {
  scheduled: ['Renews by itself', ''],
  retrying: ['Card declined: trying again', 'warn'],
  unknown: ['Checking with the card processor', 'warn'],
  renewed: ['Renewed', 'ok'],
  at_risk: ['At risk: call them', 'bad'],
  stopped: ['Not renewing', ''],
};

/** What the office can still do about a renewal in hand (books' words): only a retry takes a new card. */
export const RENEWAL_HINT: Partial<Record<RenewalState, string>> = {
  retrying: 'Choose another card to use it on the next try.',
};

/** "2 at risk" beside Renewals, or nothing. */
export const atRiskCount = (n: number) => (n > 0 ? `${n} at risk` : null);

export interface Plan {
  id: string;
  name: string;
  /** null on a plan imported bare (ServiceTitan said no term, visits or price): it sells nothing until set. */
  priceCents: number | null;
  termMonths: number | null;
  visitsPerTerm: number | null;
  /** Imported without its term, visits or price. */
  bare?: boolean;
  visitJobTypeName: string | null;
  discountPct: string;
  active: boolean;
  /** At a cancel, what was paid and not used is refunded always, by the office's choice, or never (BL-056). */
  cancelRefund?: CancelRefund;
  activeMembers: number;
}

/** How far ahead the office looks: visits due in the next 30 days, renewals in the next 60. */
export const DUE_DAYS = 30;
export const RENEWAL_DAYS = 60;

/** What a plan includes a term: "1 visit", "4 visits", "No visits". */
export function includedText(visits: number): string {
  return visits === 0 ? 'No visits' : `${visits} ${visits === 1 ? 'visit' : 'visits'}`;
}

/** "1 of 2 used", "None included". */
export function visitsText(used: number, total: number): string {
  return total === 0 ? 'None included' : `${used} of ${total} used`;
}

const day = (iso: string) => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10));

/** The members list's "Since": always with its year ("Jul 20, 2026"), however recent (history fix 1). */
export function sinceText(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${iso.slice(0, 10)}T12:00:00Z`));
}

/**
 * A membership's term (history fix 2): "Jul 20 to Jul 19" within this year, and both days with their
 * year when it isn't this year's or spans years ("Jul 20, 2026 to Jul 19, 2029"), never one without.
 */
export function termSpanText(startsOn: string, endsOn: string, thisYear = new Date().getFullYear()): string {
  if (startsOn.slice(0, 4) !== endsOn.slice(0, 4) || Number(startsOn.slice(0, 4)) !== thisYear)
    return `${sinceText(startsOn)} to ${sinceText(endsOn)}`;
  const day = (iso: string) =>
    new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
      new Date(`${iso.slice(0, 10)}T12:00:00Z`),
    );
  return `${day(startsOn)} to ${day(endsOn)}`;
}

/** Whole days from today to a date, in the company's calendar (both YYYY-MM-DD). */
export function daysUntil(date: string, today: string): number {
  return Math.round((day(date) - day(today)) / 86_400_000);
}

/** "today", "tomorrow", "in 12 days", "3 days ago". */
export function whenText(date: string, today: string): string {
  const n = daysUntil(date, today);
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  if (n === -1) return 'yesterday';
  return n > 0 ? `in ${n} days` : `${-n} days ago`;
}

/** A date YYYY-MM-DD so many days on. */
export function addDaysTo(today: string, days: number): string {
  return new Date(day(today) + days * 86_400_000).toISOString().slice(0, 10);
}

/** How many renew this soon, in days: Blueprint's figures say "the next six weeks", as the prototype. */
export const RENEW_SOON_DAYS = 42;

/** Where a page of members ends (memberships.list's `next`): its last row's sort keys. */
export interface MemberCursor {
  name: string;
  startsOn: string;
  endsOn: string;
  id: string;
}

/** The whole list's figures, every page (memberships.list works them out). */
export interface MemberFigures {
  visitsUsed: number;
  visitsTotal: number;
  autoRenew: number;
  renewSoon: number;
}

/** One page of memberships.list: its members, where the next starts, and the whole list's count and figures. */
export interface MemberPage {
  members: Member[];
  next: MemberCursor | null;
  total: number;
  figures: MemberFigures;
}

/** How many members a list reads at a time; the rest come on "Load more". */
export const MEMBER_PAGE = 100;

/** The Members list's "Show": a status, or the renewals at risk whatever their status (BL-009). */
export type MemberShow = 'active' | 'suspended' | 'cancelled' | 'expired' | 'all' | 'at_risk';

/** What the Members list asks memberships.list for: its "Show", a search when one is typed, a page. */
export function membersInput(show: MemberShow, query: string): Record<string, unknown> {
  const find = query.trim();
  return {
    ...(show === 'at_risk' ? { status: 'all', renewal: 'at_risk' } : { status: show }),
    ...(find ? { query: find } : {}),
    limit: MEMBER_PAGE,
    soonDays: RENEW_SOON_DAYS,
  };
}

/** Renewing soon: the active plans ending in the next RENEWAL_DAYS, soonest first, a page at a time. */
export const renewingInput = (today: string) => ({
  renewingWithinDays: RENEWAL_DAYS,
  asOf: today,
  limit: MEMBER_PAGE,
});

/** The renewals that didn't go through, whatever the membership's status (BL-009). */
export const AT_RISK_INPUT = { status: 'all', renewal: 'at_risk', limit: MEMBER_PAGE } as const;

/** "100 of 1,340 shown", beside "Load more". */
export const shownText = (shown: number, total: number) =>
  `${shown.toLocaleString('en-CA')} of ${total.toLocaleString('en-CA')} shown`;

/**
 * Blueprint's figures over the whole list (DESIGN.md §3, a facts strip): how many, the visits used of
 * those included, how many renew in the next six weeks, and how many renew by themselves. The server
 * counts them over every page: the page in hand is only the first of them.
 */
export function memberFacts(page: Pick<MemberPage, 'total' | 'figures'>) {
  return { members: page.total, ...page.figures };
}

// every page of a list, one after another (the new plan's service picker): shared with other lists
export { everyPage } from '../shared/pages.ts';

/** "12 months", "1 month", "2 years". */
/** A plan imported without its term, visits and price (tenant 0141): what the office still sets. */
export const NOT_SET = 'Not set';

/** A plan that can be sold: its term, visits and price are set. */
export type SellablePlan = Plan & { priceCents: number; termMonths: number; visitsPerTerm: number };
export const sellable = (p: Plan): p is SellablePlan =>
  p.priceCents !== null && p.termMonths !== null && p.visitsPerTerm !== null;

export function termText(months: number | null): string {
  if (months === null) return NOT_SET;
  if (months % 12 === 0) return months === 12 ? '1 year' : `${months / 12} years`;
  return months === 1 ? '1 month' : `${months} months`;
}

/** The plan's member discount, or nothing when it has none: "10% off". */
export function discountText(pct: string): string | null {
  const n = Number(pct);
  return n > 0 ? `${n}% off` : null;
}

/** Who adds and changes plans (memberships.manage): the owner and the manager. */
export const PLAN_PERMS = ['memberships.manage'];

/** Who sells a membership in the office (memberships.sell): the office's four who talk to customers. */
export const MEMBERSHIP_SELL_PERMS = ['memberships.sell'];

/** Who ends the plans whose term is over (memberships.expire, accounting.post): owner, manager, bookkeeper. */
export const EXPIRE_PERMS = ['accounting.post'];

/** memberships.get: one membership's term, visits and revenue. */
export interface MembershipDetail {
  id: string;
  typeId: string;
  customerId: string;
  locationId: string;
  status: string;
  startsOn: string;
  endsOn: string;
  /** BL-056: suspended since, and why; cancelled from (a date to come) or on, and why. */
  suspendedOn?: string | null;
  suspendReason?: string | null;
  cancelsOn?: string | null;
  cancelledOn?: string | null;
  cancelReason?: string | null;
  /** A cancel's refund to the customer's credit, and that credit. */
  refundedCents?: number | null;
  refundCreditId?: string | null;
  /** What a cancel would refund or keep now: deferred in the books and not used (QA-614), and how the sale stands. */
  unusedCents?: number;
  sale?: 'imported' | 'deferred' | 'draft' | 'void' | 'line_removed';
  invoiceId: string | null;
  deferredCents: number;
  recognisedCents: number;
  visitsTotal: number;
  visitsUsed: number;
  nextDueOn: string | null;
  /** Auto-renew and the customer's consent to it (F-27); `notice` says what's left to do. */
  renewal?: Renewal;
  /** It came over from ServiceTitan: ServiceTitan's while the company mirrors it (history fix 2). */
  imported?: boolean;
  /** Billed by instalments (BL-009), or null when its term is paid at once. */
  billing?: Billing | null;
}

export interface Renewal {
  on: boolean;
  consentVersion: string | null;
  consentedAt: string | null;
  /** The card renewals go to (memberships.setRenewalCard), while it's on the customer's file. */
  card: { id: string; brand: string; last4: string; label: string } | null;
  /** "Auto-renew on: add a card before Nov 5, 2027." */
  notice: string | null;
  /** On, with no card that lasts to the renewal day: the run's own check (QA-568). */
  needsCard?: boolean;
}

/**
 * The sheet's Renewal value before a renewal is under way: it renews by itself only with a card that
 * lasts to the renewal day (QA-568), else it needs one; off, the office asks them to renew.
 */
export function renewalValue(
  r: Pick<Renewal, 'on' | 'card' | 'needsCard'> | undefined,
  autoRenew: boolean,
): string {
  if (!(r?.on ?? autoRenew)) return 'Ask them to renew';
  if (r?.needsCard) return r.card ? 'Needs another card' : 'Needs a card';
  return 'Renews by itself';
}

/** memberships.renewalTerms: the words the customer agrees to, and their version (F-27). */
export interface RenewalTerms {
  version: string;
  text: string;
  amountCents: number;
  termMonths: number;
}

/** A draft invoice a membership can be sold on: an open job's. */
export interface DraftInvoice {
  invoiceId: string;
  invoiceNumber: string;
  jobId: string;
  jobNumber: string;
  summary: string;
  locationId: string;
  address: string;
}

/** "Ended 3 plans whose term was over." / "No plan was past its end date." */
export function expiredText(n: number): string {
  if (n === 0) return 'No plan was past its end date.';
  return `Ended ${n} ${n === 1 ? 'plan' : 'plans'} whose term was over. Their unused revenue is recognised.`;
}

/**
 * What a sale says about renewing (F-27): not renewing needs nothing; renewing needs the terms read
 * and the customer's yes, and then sends their version with the words exactly as shown.
 */
export function renewalConsent(
  autoRenew: boolean,
  terms: Pick<RenewalTerms, 'version' | 'text'> | null,
  agreed: boolean,
): { ready: boolean; consent?: { version: string; shownText: string } } {
  if (!autoRenew) return { ready: true };
  if (!terms || !agreed) return { ready: false };
  return { ready: true, consent: { version: terms.version, shownText: terms.text } };
}

// ── BL-056: suspend, cancel and the unused part ──

export type MemberStatus = 'active' | 'suspended' | 'expired' | 'cancelled';
export type CancelRefund = 'always' | 'optional' | 'never';

/** Who suspends, resumes and cancels a membership, and skips a visit (memberships.manage). */
export const STATUS_PERMS = PLAN_PERMS;

/** A member's status in a word or few, with the tone of its chip. */
export function statusWords(
  m: { status: MemberStatus | string; cancelsOn?: string | null },
  day: (iso: string) => string,
): [string, '' | 'ok' | 'warn'] {
  if (m.status === 'active' && m.cancelsOn) return [`Cancels on ${day(m.cancelsOn)}`, 'warn'];
  if (m.status === 'active') return ['Active', 'ok'];
  if (m.status === 'suspended') return ['Suspended', 'warn'];
  return [m.status === 'expired' ? 'Ended' : 'Cancelled', ''];
}

/** The plan's rule at a cancel, as its editor and the cancel sheet say it. */
export const CANCEL_REFUND_WORDS: Record<CancelRefund, string> = {
  always: 'Always refund what is unused',
  optional: 'Refund what is unused, unless the office refunds less',
  never: 'Never refunded: what is unused is kept',
};

/**
 * What a cancel would deal with: what was paid for the term and isn't used yet (before tax), as the
 * server works it out from the books (QA-614: nothing for a draft or voided sale); else from the term.
 */
export function unusedCents(d: {
  deferredCents: number;
  recognisedCents: number;
  refundedCents?: number | null;
  unusedCents?: number;
}) {
  if (d.unusedCents !== undefined) return d.unusedCents;
  return Math.max(0, d.deferredCents - d.recognisedCents - (d.refundedCents ?? 0));
}

/** Why a cancel refunds nothing of a sale, in words: its invoice not finalised yet, or voided. */
export const SALE_WORDS: Partial<Record<NonNullable<MembershipDetail['sale']>, string>> = {
  draft:
    'Its sale invoice isn’t finalised yet: take the membership off that invoice instead of cancelling it.',
  void: 'Its sale invoice was voided, so nothing of it is deferred to refund: it is cancelled with no refund.',
};

/** "Suspending doesn't extend the membership: it still ends on Nov 4, 2027." (the lead, BL-056) */
export const suspendNote = (endsOn: string, day: (iso: string) => string) =>
  `Suspending doesn’t extend the membership: it still ends on ${day(endsOn)}.`;

/** The refund a cancel sheet sends: cents, or a problem in words. */
export function refundAsked(
  rule: CancelRefund,
  typed: string,
  unused: number,
): { cents?: number; problem?: string } {
  if (rule !== 'optional') return {};
  const t = typed.replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return { problem: 'A refund like 120.00, or 0.' };
  const cents = Math.round(Number(t) * 100);
  if (cents > unused) return { problem: `At most what is unused: $${(unused / 100).toFixed(2)}.` };
  return { cents };
}

/** For a membership brought over from ServiceTitan (no sale invoice here): books' words. */
/**
 * Said where a cancel's refund is chosen, for a membership sold in ServiceTitan (QA-645): its unused
 * part comes back as a credit before tax; giving the tax back too is the bookkeeper's call (books).
 */
export const IMPORTED_REFUND_NOTE =
  'The unused part of a membership sold in ServiceTitan is credited before tax. Giving the tax back too is your bookkeeper’s call.';

/** The earliest and latest days a cancel can take effect: today, up to the day before the term ends. */
export function cancelDays(today: string, endsOn: string): { min: string; max: string } {
  return { min: today, max: addDaysTo(endsOn, -1) };
}

// ── BL-009: billed by instalments ──

/** Where one bill stands (memberships.bill). */
export type BillState = 'scheduled' | 'retrying' | 'unknown' | 'paid' | 'at_risk' | 'stopped';

/** memberships.get's billing: a membership billed monthly, every other month, quarterly or twice a year. */
export interface Billing {
  frequency: 'monthly' | 'every_other_month' | 'quarterly' | 'semi_annual';
  /** "monthly", "every other month", "quarterly", "twice a year". */
  words: string;
  /** One bill before tax; null until its plan has a price. */
  amountCents: number | null;
  /** The next bill date, or null when nothing more bills. */
  nextBillOn: string | null;
  billDay: number;
  endsOn: string;
  paused: { at: string; reason: string | null } | null;
  paymentKind: 'card';
  card: { id: string; label: string } | null;
  /** "Bills $20.00 monthly, next on Dec 3, 2026." */
  summary: string;
  latest: { billOn: string; state: BillState; note: string | null; invoiceId: string | null } | null;
  /** A bill not paid after its tries, its invoice still owing: the office's to collect. */
  atRisk: boolean;
}

/** The latest bill in a word or three, with the tone of its chip; the server's note says the rest. */
export const BILL_WORDS: Record<BillState, [string, '' | 'ok' | 'bad' | 'warn']> = {
  scheduled: ['Bill due', ''],
  retrying: ['Card declined: trying again', 'warn'],
  unknown: ['Checking with the card processor', 'warn'],
  paid: ['Paid', 'ok'],
  at_risk: ['Bill not paid: call them', 'bad'],
  stopped: ['Not billed', ''],
};

/** Said on an at-risk member's row, when the bill (not a renewal) is what didn't go through. */
export const BILL_AT_RISK_NOTE = 'A bill wasn’t paid after its tries. Its invoice is still owing.';

/** The days a next bill can move to: today, up to the day before the schedule ends. */
export function billDays(today: string, endsOn: string): { min: string; max: string } {
  return { min: today, max: addDaysTo(endsOn, -1) };
}

/** Why a next bill date can't be taken, in words, or null. */
export function billDateProblem(
  on: string,
  days: { min: string; max: string },
  day: (iso: string) => string,
): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(on)) return 'Choose a date.';
  if (on < days.min || on > days.max) return `A day from ${day(days.min)} to ${day(days.max)}.`;
  return null;
}
